import React, { useState, useEffect } from "react";
import { FaStar, FaPhone, FaEnvelope, FaMapMarkerAlt, FaUser, FaBuilding, FaChevronLeft } from "react-icons/fa";
import { Search, RefreshCw } from "lucide-react";
import { API_BASE } from '../config'

const CATEGORIES = [
  "ELECTRICIAN", "PLUMBER", "HOUSE CLEANING", "CARPENTER",
  "PAINTER", "PEST CONTROL", "AC SERVICE", "APPLIANCE REPAIR",
  "GARDENER", "SECURITY", "MOVING", "OTHER"
];

function StarRating({ rating = 0, size = 14, interactive = false, onRate }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <FaStar
          key={s}
          size={size}
          className={`${s <= (interactive ? (hover || rating) : Math.round(rating)) ? "text-yellow-400" : "text-gray-200"} ${interactive ? "cursor-pointer" : ""}`}
          onMouseEnter={interactive ? () => setHover(s) : undefined}
          onMouseLeave={interactive ? () => setHover(0) : undefined}
          onClick={interactive ? () => onRate(s) : undefined}
        />
      ))}
      {!interactive && rating > 0 && <span className="text-xs text-gray-500 ml-1">{Number(rating).toFixed(1)}</span>}
    </div>
  );
}

function formatCategory(c) {
  return (c || "").charAt(0).toUpperCase() + (c || "").slice(1).toLowerCase();
}

export default function ServiceContacts() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);

  const communityId = sessionStorage.getItem("community_id") || "";
  const idToken = sessionStorage.getItem("id_token") || "";

  function getUserEmail() {
    try {
      const payload = idToken.split(".")[1];
      const json = decodeURIComponent(
        atob(payload.replace(/-/g, "+").replace(/_/g, "/"))
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      return (JSON.parse(json).email || "").toLowerCase();
    } catch { return ""; }
  }

  async function apiFetch(path, opts = {}) {
    const resp = await fetch(`${API_BASE}${path}`, {
      ...opts,
      headers: { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json", ...opts.headers },
    });
    if (!resp.ok) throw new Error(`API ${resp.status}`);
    return resp.json();
  }

  async function loadPartners() {
    if (!communityId) return;
    setLoading(true);
    try {
      const data = await apiFetch(`/partners?communityId=${encodeURIComponent(communityId)}`);
      const list = Array.isArray(data) ? data : [];
      setPartners(list);
      // If a partner is currently selected, refresh it with updated data
      if (selectedPartner) {
        const updated = list.find((p) => p.PARTNER_ID === selectedPartner.PARTNER_ID);
        if (updated) setSelectedPartner(updated);
      }
    } catch (e) {
      console.error("loadPartners error", e);
    }
    setLoading(false);
  }

  useEffect(() => { loadPartners(); }, [communityId]);

  async function loadReviews(partner) {
    setLoadingReviews(true);
    try {
      const data = await apiFetch(
        `/partner-reviews?communityId=${encodeURIComponent(communityId)}&partnerId=${encodeURIComponent(partner.PARTNER_ID)}`
      );
      setReviews(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("loadReviews error", e);
      setReviews([]);
    }
    setLoadingReviews(false);
  }

  function openPartner(p) {
    setSelectedPartner(p);
    setShowReviewForm(false);
    loadReviews(p);
  }

  function goBack() {
    setSelectedPartner(null);
    setReviews([]);
    setShowReviewForm(false);
  }

  const filtered = partners.filter((p) => {
    if (p.STATUS === "INACTIVE") return false;
    const matchSearch = !searchTerm ||
      (p.NAME || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.CATEGORY || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchCat = filterCategory === "all" || (p.CATEGORY || "").toUpperCase() === filterCategory;
    return matchSearch && matchCat;
  });

  // ---- Detail / Reviews view ----
  if (selectedPartner) {
    return (
      <div className="p-4">
        <button onClick={goBack} className="flex items-center gap-1 text-sm text-blue-600 mb-4">
          <FaChevronLeft size={12} /> Back to contacts
        </button>

        {/* Partner card */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
          <div className="flex items-start gap-3">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${selectedPartner.TYPE === "COMPANY" ? "bg-blue-100 text-blue-600" : "bg-blue-100 text-blue-600"}`}>
              {selectedPartner.TYPE === "COMPANY" ? <FaBuilding size={20} /> : <FaUser size={20} />}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-bold text-base">{selectedPartner.NAME}</h2>
              <span className="inline-block px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full mt-0.5">
                {formatCategory(selectedPartner.CATEGORY)}
              </span>
              <div className="mt-1 flex items-center gap-1">
                <StarRating rating={parseFloat(selectedPartner.AVG_RATING || "0")} />
                <span className="text-xs text-gray-400">({selectedPartner.TOTAL_REVIEWS || 0} reviews)</span>
              </div>
            </div>
          </div>

          <div className="mt-3 space-y-2 text-sm text-gray-600">
            {selectedPartner.PHONE && (
              <div className="flex items-center gap-2">
                <FaPhone size={12} className="text-gray-400" />
                <a href={`tel:${selectedPartner.PHONE}`} className="text-blue-600">{selectedPartner.PHONE}</a>
              </div>
            )}
            {selectedPartner.EMAIL && (
              <div className="flex items-center gap-2">
                <FaEnvelope size={12} className="text-gray-400" />
                <a href={`mailto:${selectedPartner.EMAIL}`} className="text-blue-600">{selectedPartner.EMAIL}</a>
              </div>
            )}
            {selectedPartner.ADDRESS && (
              <div className="flex items-center gap-2">
                <FaMapMarkerAlt size={12} className="text-gray-400" />
                <span>{selectedPartner.ADDRESS}</span>
              </div>
            )}
            {selectedPartner.DESCRIPTION && <p className="text-gray-500 text-xs mt-1">{selectedPartner.DESCRIPTION}</p>}
          </div>
        </div>

        {/* Reviews section */}
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-sm">Reviews</h3>
          {!showReviewForm && (
            <button
              onClick={() => setShowReviewForm(true)}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium active:bg-blue-700"
            >
              Write a Review
            </button>
          )}
        </div>

        {showReviewForm && (
          <ReviewForm
            communityId={communityId}
            partner={selectedPartner}
            userEmail={getUserEmail()}
            apiFetch={apiFetch}
            onDone={() => {
              setShowReviewForm(false);
              loadReviews(selectedPartner);
              loadPartners(); // refresh avg rating
            }}
            onCancel={() => setShowReviewForm(false)}
          />
        )}

        {loadingReviews ? (
          <div className="text-center py-6 text-gray-400 text-sm">Loading reviews...</div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-6 text-gray-400 text-sm">No reviews yet. Be the first to review!</div>
        ) : (
          <div className="space-y-3">
            {reviews.map((r) => (
              <div key={r.REVIEW_ID} className="bg-white border border-gray-200 rounded-xl p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-700">{r.REVIEWER_EMAIL || "Anonymous"}</span>
                  <span className="text-xs text-gray-400">{r.CREATED_AT ? new Date(r.CREATED_AT).toLocaleDateString() : ""}</span>
                </div>
                <StarRating rating={parseInt(r.RATING || "0", 10)} size={12} />
                {r.COMMENT && <p className="text-xs text-gray-600 mt-1">{r.COMMENT}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ---- List view ----
  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold">Service Contacts</h2>
        <button onClick={loadPartners} className="p-2 rounded-lg bg-gray-100 active:bg-gray-200">
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* Search & Filter */}
      <div className="flex gap-2 mb-4">
        <div className="flex-1 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search contacts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm"
          />
        </div>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="border rounded-lg px-2 py-2 text-sm bg-white"
        >
          <option value="all">All</option>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>{formatCategory(c)}</option>
          ))}
        </select>
      </div>

      {/* Contact cards */}
      {loading ? (
        <div className="text-center py-8 text-gray-400">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <FaPhone size={28} className="mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No service contacts available</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((p) => (
            <div
              key={p.PARTNER_ID}
              onClick={() => openPartner(p)}
              className="bg-white border border-gray-200 rounded-xl p-4 active:bg-gray-50 cursor-pointer"
            >
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${p.TYPE === "COMPANY" ? "bg-blue-100 text-blue-600" : "bg-blue-100 text-blue-600"}`}>
                  {p.TYPE === "COMPANY" ? <FaBuilding size={16} /> : <FaUser size={16} />}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm truncate">{p.NAME}</h3>
                  <span className="inline-block px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full mt-0.5">
                    {formatCategory(p.CATEGORY)}
                  </span>
                  <div className="flex items-center gap-1 mt-0.5">
                    <StarRating rating={parseFloat(p.AVG_RATING || "0")} size={12} />
                    <span className="text-xs text-gray-400">({p.TOTAL_REVIEWS || 0})</span>
                  </div>
                </div>
                {p.PHONE && (
                  <a
                    href={`tel:${p.PHONE}`}
                    onClick={(e) => e.stopPropagation()}
                    className="w-9 h-9 rounded-full bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0"
                  >
                    <FaPhone size={14} />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReviewForm({ communityId, partner, userEmail, apiFetch, onDone, onCancel }) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (rating === 0) return;
    setSaving(true);
    try {
      await apiFetch("/partner-reviews", {
        method: "POST",
        body: JSON.stringify({
          COMMUNITY_ID: communityId,
          PARTNER_ID: partner.PARTNER_ID,
          REVIEWER_EMAIL: userEmail,
          RATING: String(rating),
          COMMENT: comment.trim(),
        }),
      });
      onDone();
    } catch (e) {
      alert("Failed to submit review");
    }
    setSaving(false);
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
      <h4 className="font-medium text-sm mb-2">Your Rating</h4>
      <StarRating rating={rating} size={24} interactive onRate={setRating} />
      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Write your comment (optional)..."
        rows={3}
        className="w-full mt-3 px-3 py-2 border rounded-lg text-sm resize-none"
      />
      <div className="flex gap-2 mt-3">
        <button
          type="submit"
          disabled={rating === 0 || saving}
          className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium disabled:opacity-50 active:bg-blue-700"
        >
          {saving ? "Submitting..." : "Submit Review"}
        </button>
        <button type="button" onClick={onCancel} className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm">
          Cancel
        </button>
      </div>
    </form>
  );
}
